import { defineStore } from "pinia";
import {
  doc,
  query,
  collection,
  updateDoc,
  addDoc,
  setDoc,
  orderBy,
  limit,
  deleteDoc,
  where,
} from "firebase/firestore";
import { db, converter, auth } from "@/firebase";
import { useFirestore } from "@vueuse/firebase";
import { useAuthStore } from "./auth";
import { computed, Ref, ref, watch } from "vue";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getEnvCollection, getRootCollection, ENV_COLLECTIONS, ROOT_COLLECTIONS } from "@/firebase/collections";
import {
  Account,
  Company,
  Description,
  Environment,
  MachineType,
  Product,
  XeroToken,
  XeroCustomer,
  XeroItem,
  ChartAccount,
} from "@/models";
import { useRoute } from "vue-router";
import * as Sentry from "@sentry/vue";

function isCallableTransportError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const { code, message } = error as Record<string, unknown>;
  // Firebase maps a rejected fetch to status 0. Server INTERNAL errors have
  // an HTTP status such as [500] and must not be retried by this path.
  return code === "functions/internal" && message === "internal [0]";
}

function extractFirebaseErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === "object") {
    const errorObject = error as Record<string, unknown>;
    const directMessage = errorObject.message;
    if (typeof directMessage === "string" && directMessage.trim().length > 0) {
      return directMessage;
    }

    const nestedError = errorObject.error;
    if (nestedError && typeof nestedError === "object") {
      const nestedMessage = (nestedError as Record<string, unknown>).message;
      if (typeof nestedMessage === "string" && nestedMessage.trim().length > 0) {
        return nestedMessage;
      }
    }

    const details = errorObject.details;
    if (details && typeof details === "object") {
      const detailMessage = (details as Record<string, unknown>).message;
      if (typeof detailMessage === "string" && detailMessage.trim().length > 0) {
        return detailMessage;
      }
    }
  }

  return fallback;
}

export const useAdminStore = defineStore("admin", () => {
  const authStore = useAuthStore();
  const authUser = computed(() => authStore.authenticatedUser);
  const functions = getFunctions();

  const userId = ref<string>();

  watch(() => authStore.authenticatedUser, (newUser) => {
    userId.value = newUser?.uid || '';
  }, { immediate: true });

  const userQuery = computed(
    () =>
      !!userId.value &&
      doc(db, getRootCollection('USERS'), userId.value).withConverter(converter<User>())
  );
  const user = useFirestore<User>(userQuery);

  const route = useRoute();
  const envId = ref<string | undefined>();

  const routeEnvId = computed(() => {
    const routeParam = route.params?.envid;
    if (Array.isArray(routeParam)) {
      return routeParam[0];
    }
    return routeParam as string | undefined;
  });

  watch(
    [() => user.value?.environment, routeEnvId],
    ([userEnvId, paramEnvId]) => {
      // User profile environment is the source of truth once available.
      const resolvedEnvId = userEnvId || paramEnvId || envId.value;

      if (resolvedEnvId && resolvedEnvId !== envId.value) {
        envId.value = resolvedEnvId;
      }
    },
    { immediate: true }
  );

  const chartOfAccountsQuery = computed(() =>
    !!envId.value &&
    query(
      collection(db, getEnvCollection(envId.value, 'CHART_OF_ACCOUNTS')),
      where("status" , "==", "ACTIVE")
    ).withConverter(converter<ChartAccount>())
  );

  const chartOfAccounts = useFirestore<ChartAccount>(chartOfAccountsQuery);

  const xeroItemsQuery = computed(() =>
    !!envId.value &&
    query(
      collection(db, getEnvCollection(envId.value, 'XERO_ITEMS')),
      orderBy("name")
    ).withConverter(converter<XeroItem>())
  );

  const xeroItems = useFirestore<XeroItem>(xeroItemsQuery);

  async function saveDefaultAccount(account: ChartAccount): Promise<void> {
    try {
      if (!envId.value) {
        throw new Error("Environment ID is not set");
      }

      //Save to enviroment document
      const envRef = doc(db, getRootCollection('ENVIRONMENTS'), envId.value);
      await updateDoc(envRef, {
        defaultAccount: account,
        updatedAt: new Date(),
      });
      return;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  // Wrapper to handle Xero authentication errors and prompt re-authorization
  async function handleXeroAuthError(operation: () => Promise<any>): Promise<any> {
    try {
      return await operation();
    } catch (error) {
      Sentry.captureException(error);
      const errorMessage = error instanceof Error ? error.message : '';
      
      // Check if this is a re-authorization error
      if (errorMessage.includes('re-authorize') || errorMessage.includes('authentication failed')) {
        isReauthorizing.value = true;
        
        // Ask user to reconnect
        const shouldReconnect = confirm(
          'Your Xero connection has expired. Would you like to reconnect now?'
        );
        
        if (shouldReconnect) {
          try {
            const authenticateXero = httpsCallable(functions, 'xero-generateXeroConsentUrl');
            const result = await authenticateXero({
              userId: auth.currentUser?.uid,
              environmentId: envId.value,
              redirectUrl: window.location.origin + '/xero-callback',
            });
            const { consentUrl } = result.data as any;
            window.location.href = consentUrl;
            // Throw error to prevent caller from continuing (we're redirecting anyway)
            throw new Error('Redirecting to Xero for re-authorization...');
          } catch (consentError) {
            console.error('Error generating consent URL:', consentError);
            Sentry.captureException(consentError);
            isReauthorizing.value = false;
            throw new Error('Failed to initiate Xero reconnection. Please try from Admin > Xero Settings.');
          }
        } else {
          isReauthorizing.value = false;
          throw new Error('Xero connection expired. Please reconnect in Admin > Xero Settings.');
        }
      }
      
      // Re-throw other errors
      throw error;
    }
  }

  // Function to call pullXeroChartOfAccounts firebase function

  async function fetchChartOfAccounts(): Promise<void> {
    return handleXeroAuthError(async () => {
      try {
        const pullXeroChartOfAccounts = httpsCallable(functions, "xero-pullXeroChartOfAccounts");
        const result = await pullXeroChartOfAccounts({
          environmentId: envId.value,
        });
      } catch (error) {
        console.error("Error fetching Chart of Accounts:", error);
        Sentry.captureException(error);
        const errorMessage = extractFirebaseErrorMessage(
          error,
          "Failed to sync chart of accounts. Please try again."
        );
        throw new Error(errorMessage);
      }
    });
  }

  // Function to call pullXeroItems firebase function
  async function fetchXeroItems(): Promise<void> {
    return handleXeroAuthError(async () => {
      try {
   
        const pullXeroItems = httpsCallable(functions, "xero-pullXeroItems");
        const result = await pullXeroItems({
          environmentId: envId.value,
        });
      } catch (error) {
        console.error("Error fetching Xero Items:", error);
        Sentry.captureException(error);
        const errorMessage = extractFirebaseErrorMessage(
          error,
          "Failed to sync Xero items. Please try again."
        );
        throw new Error(errorMessage);
      }
    });
  }

  // Function to link a product to a Xero item
  async function linkProductToXeroItem(productId: string, xeroItemId: string): Promise<void> {
    return handleXeroAuthError(async () => {
      try {
        const linkProductToItem = httpsCallable(functions, "xero-linkProductToItem");
        const result = await linkProductToItem({
          environmentId: envId.value,
          productId,
          xeroItemId,
        });
      } catch (error) {
        console.error("Error linking product to Xero item:", error);
        Sentry.captureException(error);
        const errorMessage = extractFirebaseErrorMessage(
          error,
          "Failed to link product to Xero item. Please try again."
        );
        throw new Error(errorMessage);
      }
    });
  }

  // Function to unlink a product from a Xero item
  async function unlinkProductFromXeroItem(productId: string): Promise<void> {
    return handleXeroAuthError(async () => {
      try {
        const unlinkProductFromItem = httpsCallable(functions, "xero-unlinkProductFromItem");
        const result = await unlinkProductFromItem({
          environmentId: envId.value,
          productId,
        });
      } catch (error) {
        console.error("Error unlinking product from Xero item:", error);
        Sentry.captureException(error);
        const errorMessage = extractFirebaseErrorMessage(
          error,
          "Failed to unlink product from Xero item. Please try again."
        );
        throw new Error(errorMessage);
      }
    });
  }
    

  const envQuery = computed(() => {
    return envId.value
      ? doc(db, getRootCollection('ENVIRONMENTS'), envId.value).withConverter(
        converter<Environment>()
      )
      : undefined;
  });

  const environment = useFirestore<Environment>(envQuery);

  function triggerXeroTokenCheck() {
    forceXeroTokenCheck.value++;
  }

  // Function to start periodic token checks
  function startPeriodicTokenCheck() {
    // Check every 30 minutes
    setInterval(() => {
      if (envId.value) {
        triggerXeroTokenCheck();
      }
    }, 30 * 60 * 1000); // 30 minutes
  }

  // Start periodic checks when the store is initialized
  startPeriodicTokenCheck();

  // Watch for environment ID changes and trigger Xero token check
  watch(envId, (newEnvId) => {
    if (newEnvId) {
      // Small delay to ensure xeroTokens query is set up
      setTimeout(() => {
        triggerXeroTokenCheck();
      }, 500);
    }
  });

  const productsQuery = computed(
    () =>
      !!envId.value &&
      query(
        collection(db, getEnvCollection(envId.value, 'PRODUCTS')),
        orderBy("Description")
      ).withConverter(converter<Product>())
  );
  const products = useFirestore<Product>(productsQuery);

  const machinesQuery = computed(
    () =>
      !!envId.value &&
      query(
        collection(db, getEnvCollection(envId.value, 'MACHINE_TYPES')),
        orderBy("description")
      ).withConverter(converter<MachineType>())
  );
  const machineTypes = useFirestore<MachineType>(machinesQuery);

  const descriptionsQuery = computed(
    () =>
      !!envId.value &&
      query(
        collection(db, getEnvCollection(envId.value, 'DESCRIPTIONS')),
        orderBy("description")
      ).withConverter(converter<Description>())
  );
  const descriptions = useFirestore<Description>(descriptionsQuery);

  const accountsQuery = computed(
    () =>
      !!envId.value &&
      query(
        collection(db, getEnvCollection(envId.value, 'ACCOUNTS')),
        orderBy("name")
      ).withConverter(converter<Account>())
  );

  let accounts: Ref<Account[] | undefined> = useFirestore<Account>(accountsQuery);

  const companiesQuery = computed(
    () =>
      !!envId.value &&
      query(
        collection(db, getEnvCollection(envId.value, 'COMPANIES'))
      ).withConverter(converter<Company>())
  );

  const allCompanies = useFirestore<Company>(companiesQuery);

  // Filtered companies based on environment setting (for dropdowns/lists)
  const companies = computed(() => {
    if (!allCompanies.value || allCompanies.value.length === 0) return [];
    
    // Only filter if the setting is explicitly configured
    // If undefined/null, show ALL companies (backwards compatible)
    if (environment.value?.useXeroCustomers === true) {
      // Xero mode: show only Xero customers
      return allCompanies.value.filter(c => c.type === 'xero' || c.type === 'non-billable');
    } else if (environment.value?.useXeroCustomers === false) {
      // Cashbook mode: show cashbook and non-billable customers (exclude Xero)
      return allCompanies.value.filter(c => c.type === 'cashbook' || c.type === 'non-billable');
    }
    
    // Default: show ALL companies (for existing installations without the setting)
    return allCompanies.value;
  });

  // Helper to get a specific company by ID (bypasses filtering for historical data)
  function getCompanyById(companyId: string): Company | undefined {
    return allCompanies.value?.find(c => c.id === companyId);
  }

  // Xero token query
  const xeroTokenQuery = computed(
    () =>
      !!envId.value &&
      query(
        collection(db, getEnvCollection(envId.value, 'XERO_TOKENS')),
        orderBy("expires_at", "desc"),
        limit(1)
      ).withConverter(converter<XeroToken>())
  );

  const xeroTokens = useFirestore<XeroToken>(xeroTokenQuery);

  // Watch for xeroTokens to be loaded and trigger initial check
  watch(xeroTokens, (newTokens) => {
    if (newTokens !== undefined) {
      triggerXeroTokenCheck();
    }
  }, { immediate: true });

  // Watch for environment changes and trigger token check
  watch(() => environment.value?.id, (newEnvId) => {
    if (newEnvId) {
      // Small delay to ensure xeroTokens query is set up
      setTimeout(() => {
        triggerXeroTokenCheck();
      }, 500);
    }
  }, { immediate: true });

  // Xero token refresh state
  const isRefreshingXeroToken = ref(false);
  const xeroTokenRefreshError = ref<string | null>(null);
  const forceXeroTokenCheck = ref(0); // Trigger for forcing token validation
  const isReauthorizing = ref(false);

  // Check if Xero is connected and token is valid
  const isXeroConnected = computed(() => {
    // This line ensures the computed property reacts to manual triggers
    forceXeroTokenCheck.value;

    // If xeroTokens is still undefined (loading), assume connected
    // The actual connection will be validated by the backend
    if (xeroTokens.value === undefined) {
      return true;
    }

    if (!xeroTokens.value || xeroTokens.value.length === 0) {
      return false;
    }

    const token = xeroTokens.value[0];
    const now = Date.now() / 1000; // Convert to seconds

    return token.expires_at > now;
  });

  const isXeroLinked = computed(() => {
    return !!xeroTokens.value && xeroTokens.value.length > 0;
  })

  const lastXeroRefresh = computed(() => {
    if (!xeroTokens.value || xeroTokens.value.length === 0) return null;

    const token = xeroTokens.value[0];
    return token.refreshedAt;
  })

  // Check if manifest field is enabled for this environment
  const isManifestEnabled = computed(() => {
    return environment.value?.enableManifest ?? false;
  });

  const consolidateInvoiceLineItems = computed(() => {
    return environment.value?.consolidateLineItems ?? true;
  });

  // Check if Xero customers mode is enabled for this environment
  const useXeroCustomers = computed(() => {
    return environment.value?.useXeroCustomers ?? false;
  });

  const isReferenceNumberEnabled = computed(() => {
    return environment.value?.enablereferenceNumber ?? false;
  });

  const isMapsGoEnabled = computed(() => {
    return environment.value?.enableMapsGo ?? false;
  });

  const showNextDayJobs = computed(() => {
    return environment.value?.showNextDayJobs ?? true;
  });

  // Function to refresh Xero token
  async function refreshXeroToken(): Promise<boolean> {
    if (isRefreshingXeroToken.value) return false;

    isRefreshingXeroToken.value = true;
    xeroTokenRefreshError.value = null;

    try {
      const refreshXeroToken = httpsCallable(functions, "xero-refreshXeroToken");
      const result = await refreshXeroToken({
        userId: auth.currentUser?.uid,
        environmentId: envId.value,
      });


      // Force the computed property to re-evaluate
      forceXeroTokenCheck.value++;

      // Token should be automatically updated in Firestore, 
      // and our reactive query will pick up the changes
      return (result.data as any).success || false;
    } catch (error) {
      console.error("Error refreshing Xero token:", error);
      Sentry.captureException(error);
      xeroTokenRefreshError.value = error instanceof Error ? error.message : "Failed to refresh token";
      return false;
    } finally {
      isRefreshingXeroToken.value = false;
    }
  }

  // Function to check and refresh Xero token if needed
  async function ensureValidXeroToken(): Promise<boolean> {
    // Force a check first to get the latest state
    triggerXeroTokenCheck();

    // Wait a moment for the reactive property to update
    await new Promise(resolve => setTimeout(resolve, 100));

    // If already connected and valid, return true
    if (isXeroConnected.value) return true;

    // If no token exists at all, user needs to re-authenticate
    if (!xeroTokens.value || xeroTokens.value.length === 0) {
      xeroTokenRefreshError.value = "No Xero token found. Please re-authenticate with Xero.";
      return false;
    }

    // Try to refresh the token
    const refreshSuccess = await refreshXeroToken();

    if (!refreshSuccess) {
      xeroTokenRefreshError.value = "Token refresh failed. Please re-authenticate with Xero.";
      return false;
    }

    // Wait a moment for the reactive query to update, then check again
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (!isXeroConnected.value) {
      xeroTokenRefreshError.value = "Token refresh completed but token is still invalid. Please re-authenticate with Xero.";
      return false;
    }

    return true;
  }

  // Function to perform an immediate token check and return status
  async function checkXeroConnectionStatus(): Promise<boolean> {
    triggerXeroTokenCheck();

    // Wait a moment for reactive updates
    await new Promise(resolve => setTimeout(resolve, 100));

    return isXeroConnected.value;
  }

  const initialXeroCheckDone = ref(false);

  async function initializeXeroState(): Promise<void> {
    if (initialXeroCheckDone.value) return;
    
    if (xeroTokens.value === undefined) {
      for (let i = 0; i < 30; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        if (xeroTokens.value !== undefined) break;
      }
    }
    
    triggerXeroTokenCheck();
    initialXeroCheckDone.value = true;
  }

  watch(envId, () => {
    initialXeroCheckDone.value = false;
  });

  const setEnvID = (id: string) => {
    envId.value = id;
  };

  async function saveProduct(product: Product): Promise<void> {
    try {
      const operatorRef = doc(
        db,
        getEnvCollection(envId.value!, 'PRODUCTS'),
        product.id
      );
      return await updateDoc(operatorRef, {
        ...product,
        updatedAt: new Date(),
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  async function saveMachine(machine: MachineType): Promise<void> {
    try {
      const machineRef = doc(
        db,
        getEnvCollection(envId.value!, 'MACHINE_TYPES'),
        machine.id
      );
      return await updateDoc(machineRef, {
        ...machine,
        updatedAt: new Date(),
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  async function saveEnvironment(environment: Environment): Promise<void> {
    try {
      const envRef = doc(db, getRootCollection('ENVIRONMENTS'), environment.id);
      return await updateDoc(envRef, {
        ...environment,
        updatedAt: new Date(),
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  async function createDescription(
    description: Partial<Description>
  ): Promise<void> {
    try {
      const collectionRef = getEnvCollection(envId.value!, 'DESCRIPTIONS');
      await addDoc(collection(db, collectionRef), {
        ...description,
        updatedAt: new Date(),
      });
      return;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }
  async function createMachine(machine: Partial<MachineType>): Promise<void> {
    try {
      const collectionRef = getEnvCollection(envId.value!, 'MACHINE_TYPES');
      await addDoc(collection(db, collectionRef), {
        ...machine,
        updatedAt: new Date(),
      });
      return;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  // Create a simple account (just name and id)
  async function createAccount(account: Partial<Account>): Promise<void> {
    try {
      const collectionRef = getEnvCollection(envId.value!, 'ACCOUNTS');
      await addDoc(collection(db, collectionRef), {
        name: account.name,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      return;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  async function saveAccount(account: Account): Promise<void> {
    try {
      const accountRef = doc(
        db,
        getEnvCollection(envId.value!, 'ACCOUNTS'),
        account.id
      );
      return await updateDoc(accountRef, {
        ...account,
        updatedAt: new Date(),
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  async function saveDescription(description: Description): Promise<void> {
    try {
      const descriptionRef = doc(
        db,
        getEnvCollection(envId.value!, 'DESCRIPTIONS'),
        description.id
      );
      return await updateDoc(descriptionRef, {
        ...description,
        updatedAt: new Date(),
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  // Function to fetch Xero customers
  async function fetchXeroCustomers(): Promise<XeroCustomer[]> {
    try {
      // Ensure we have a valid token before making the request
      const hasValidToken = await ensureValidXeroToken();
      if (!hasValidToken) {
        throw new Error(xeroTokenRefreshError.value || "Xero authentication required");
      }

      const pullXeroCustomers = httpsCallable(functions, "xero-pullXeroCustomers");
      const data = {
        userId: auth.currentUser?.uid,
        environmentId: envId.value,
      };
      let result;
      try {
        result = await pullXeroCustomers(data);
      } catch (error) {
        if (!isCallableTransportError(error)) throw error;
        // This call only reads contacts. Retry once after a brief connection
        // interruption; never apply this policy to invoice/company writes.
        await new Promise(resolve => setTimeout(resolve, 500));
        result = await pullXeroCustomers(data);
      }
      return (result.data as any).customers || [];
    } catch (error) {
      console.error("Error fetching Xero customers:", error);
      Sentry.captureException(error);
      if (isCallableTransportError(error)) {
        throw new Error("Unable to load Xero companies. Check your connection and try again.", { cause: error });
      }
      throw error;
    }
  }

  // Function to delete an account
  async function deleteAccount(accountId: string): Promise<void> {
    try {
      const accountRef = doc(db, getEnvCollection(envId.value!, 'ACCOUNTS'), accountId);
      await deleteDoc(accountRef);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  // Company management functions
  async function addCompany(companyData: Omit<Company, "id">): Promise<void> {
    try {
      await addDoc(collection(db, getEnvCollection(envId.value!, 'COMPANIES')), companyData);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  async function deleteCompany(companyId: string): Promise<void> {
    try {
      const companyRef = doc(db, getEnvCollection(envId.value!, 'COMPANIES'), companyId);
      await deleteDoc(companyRef);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  async function updateCompany(companyId: string, companyData: Partial<Company>): Promise<void> {
    try {
      const companyRef = doc(db, getEnvCollection(envId.value!, 'COMPANIES'), companyId);
      await updateDoc(companyRef, {
        ...companyData,
        updatedAt: new Date(),
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  // Function to clear Xero token refresh error
  function clearXeroTokenError() {
    xeroTokenRefreshError.value = null;
  }

  // Function to revoke Xero connection
  async function revokeXeroConnection(): Promise<void> {
    try {
      const revokeXeroConnection = httpsCallable(functions, "xero-revokeXeroConnection");
      await revokeXeroConnection({
        userId: auth.currentUser?.uid,
        environmentId: envId.value,
      });

      // Clear any cached token data and force a recheck
      xeroTokenRefreshError.value = null;
      if (xeroTokens.value) {
        xeroTokens.value = [];
      }
      triggerXeroTokenCheck();

    } catch (error) {
      console.error("Error revoking Xero connection:", error);
      Sentry.captureException(error);
      const errorMessage = extractFirebaseErrorMessage(
        error,
        "Failed to revoke Xero connection. Please try again."
      );
      throw new Error(errorMessage);
    }
  }

  const clear = () => {
    user.value = undefined;
    environment.value = undefined;
    envId.value = undefined;
    products.value = [];
    machineTypes.value = [];
    descriptions.value = [];
    accounts.value = [];
    allCompanies.value = [];
  };

  const isJobInvoicingEnabled = computed(() => {
    return environment.value?.jobInvoicing ?? false;
  })


  return {
    user,
    environment,
    envId,
    products,
    machineTypes,
    descriptions,
    accounts,
    chartOfAccounts,
    companies,
    allCompanies,
    xeroTokens,
    isXeroConnected,
    isXeroLinked,
    lastXeroRefresh,
    isRefreshingXeroToken,
    xeroTokenRefreshError,
    isReauthorizing,
    isManifestEnabled,
    consolidateInvoiceLineItems,
    useXeroCustomers,
    isJobInvoicingEnabled,
    isReferenceNumberEnabled,
    isMapsGoEnabled,
    showNextDayJobs,
    saveDefaultAccount,
    fetchChartOfAccounts,
    fetchXeroItems,
    xeroItems,
    linkProductToXeroItem,
    unlinkProductFromXeroItem,
    handleXeroAuthError,
    setEnvID,
    saveProduct,
    saveMachine,
    saveAccount,
    saveEnvironment,
    saveDescription,
    createDescription,
    createAccount,
    createMachine,
    fetchXeroCustomers,
    deleteAccount,
    addCompany,
    deleteCompany,
    updateCompany,
    refreshXeroToken,
    ensureValidXeroToken,
    checkXeroConnectionStatus,
    triggerXeroTokenCheck,
    clearXeroTokenError,
    revokeXeroConnection,
    getCompanyById,
    clear,
    initializeXeroState,
  };
});

export interface User {
  id: string;
  environment: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  username: string;
  lastSeenVersion?: string; // Track which changelog version user has seen
}
